import React, { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Bookmark, Share2, Sparkles, MapPin, Calendar, HelpCircle, Briefcase, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import SafeImage from "./SafeImage";
import AiPhotoBadge from "./AiPhotoBadge";
import GuiaNewsAIBadge from "./GuiaNewsAIBadge";

const CATEGORY_LABELS = {
  "onde-comer": "Onde comer", eventos: "Eventos", promocoes: "Promoções",
  empresas: "Empresas", hoteis: "Hotéis", praias: "Praias", imoveis: "Imóveis",
  empregos: "Empregos", saude: "Saúde", noticias: "Notícias", turismo: "Turismo",
};

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch { return ""; }
}

export default function FeedCard({ post, index = 0, onAskAI }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(post.is_liked || false);
  const [saved, setSaved] = useState(post.is_saved || false);
  const [likes, setLikes] = useState(post.likes || 0);

  const requireAuth = () => {
    if (!user) { toast("Entre para interagir", { description: "Faça login para curtir e salvar." }); navigate("/login"); return false; }
    return true;
  };

  const toggleLike = async () => {
    if (!requireAuth()) return;
    setLiked((v) => !v);
    setLikes((n) => (liked ? n - 1 : n + 1));
    try { await api.post(`/feed/${post.id}/like`); } catch { toast.error("Erro ao curtir"); }
  };

  const toggleSave = async () => {
    if (!requireAuth()) return;
    setSaved((v) => !v);
    try {
      await api.post(`/feed/${post.id}/save`);
      toast.success(saved ? "Removido dos favoritos" : "Salvo nos favoritos");
    } catch { toast.error("Erro ao salvar"); }
  };

  const shareUrl = post.slug ? `${window.location.origin}/api/share/noticia/${post.slug}` : `${window.location.origin}/feed`;
  const share = async () => {
    const text = `Confira esta notícia no Guia de Jampa AI: ${post.title}`;
    try {
      if (navigator.share) await navigator.share({ title: post.title, text, url: shareUrl });
      else { await navigator.clipboard.writeText(`${text} ${shareUrl}`); toast.success("Link copiado!"); }
    } catch (e) {
      if (e?.name !== "AbortError" && process.env.NODE_ENV !== "production") console.debug("Share failed:", e);
    }
  };
  const openNews = () => { if (post.slug) navigate(`/noticia/${post.slug}`); };

  return (
    <motion.article
      data-testid={`feed-card-${post.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.3) }}
      className="bg-card rounded-3xl overflow-hidden border border-border soft-shadow"
    >
      <div className="relative">
        <SafeImage src={post.image} category={post.category} alt={post.title} loading="lazy"
          onClick={openNews}
          className="w-full h-60 md:h-72 object-cover bg-muted cursor-pointer" />
        {post.ai_image && <AiPhotoBadge />}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="rounded-full bg-black/55 backdrop-blur-md text-white text-xs font-semibold px-3 py-1.5">
            {CATEGORY_LABELS[post.category] || post.category}
          </span>
          {post.guia_news_ai && <GuiaNewsAIBadge />}
        </div>
        <button
          data-testid={`view-source-${post.id}`}
          onClick={openNews}
          className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full bg-white/90 dark:bg-black/70 backdrop-blur-md text-foreground text-xs font-bold px-3 py-1.5 active:scale-95 transition-transform"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Ler notícia
        </button>
        {post.source && (
          <span className="absolute bottom-3 left-4 rounded-full bg-black/60 backdrop-blur-md text-white text-[11px] font-medium px-2.5 py-1">
            Fonte: {post.source}
          </span>
        )}
      </div>
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {post.location || post.city}</span>
          {post.date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(post.date)}</span>}
        </div>
        <h3 onClick={openNews} className="font-heading font-bold text-lg md:text-xl leading-snug mb-2 cursor-pointer hover:text-primary transition-colors">{post.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{post.summary}</p>

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {post.tags.slice(0, 3).map((t) => (
              <span key={t} className="text-[11px] rounded-full bg-secondary text-secondary-foreground px-2.5 py-1">#{t}</span>
            ))}
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-2">
          <button
            data-testid={`ask-ai-post-${post.id}`}
            onClick={() => navigate(`/ia?q=${encodeURIComponent(`Me conte mais sobre "${post.title}" em ${post.location || post.city}. O que você recomenda?`)}`)}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground py-2.5 text-sm font-semibold active:scale-[0.98] transition-transform"
          >
            <Sparkles className="w-4 h-4" /> Perguntar à IA sobre isso
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid={`explain-post-${post.id}`}
              onClick={() => navigate(`/ia?q=${encodeURIComponent(`Explique melhor, de forma simples, essa novidade: "${post.title}". Contexto: ${post.summary}`)}`)}
              className="flex items-center justify-center gap-1.5 rounded-full border border-border py-2 text-xs font-semibold text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <HelpCircle className="w-3.5 h-3.5" /> Explicar melhor
            </button>
            <button
              data-testid={`impact-post-${post.id}`}
              onClick={() => navigate(`/ia?q=${encodeURIComponent(`Como isso pode impactar o meu negócio em João Pessoa? Novidade: "${post.title}". ${post.summary}`)}`)}
              className="flex items-center justify-center gap-1.5 rounded-full border border-border py-2 text-xs font-semibold text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Briefcase className="w-3.5 h-3.5" /> Impacto no negócio
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
          <button data-testid={`like-${post.id}`} onClick={toggleLike} aria-label="Curtir" className="flex items-center gap-1.5 text-sm active:scale-90 transition-transform">
            <Heart className={`w-5 h-5 ${liked ? "fill-alert text-alert" : "text-muted-foreground"}`} />
            <span className="text-muted-foreground">{likes}</span>
          </button>
          <button data-testid={`save-${post.id}`} onClick={toggleSave} aria-label="Salvar" className="active:scale-90 transition-transform">
            <Bookmark className={`w-5 h-5 ${saved ? "fill-primary text-primary" : "text-muted-foreground"}`} />
          </button>
          <button data-testid={`share-${post.id}`} onClick={share} aria-label="Compartilhar" className="active:scale-90 transition-transform">
            <Share2 className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
      </div>
    </motion.article>
  );
}

export { CATEGORY_LABELS };
